import ContentHeader from '@/components/ContentHeader';
import { DatePicker } from '@/components/DatePicker';
import MemberCardKeys from '@/components/MemberCardKeys';
import MembershipCard from '@/components/MembershipCard';
import { modalType } from '@/components/Modal';
import { absoluteServerPath, resolveConfigUrl } from '@/helpers/api';
import { cacheFetch } from '@/helpers/cacheFetch';
import { useMobxStores } from '@/stores';
import { IAuthUser } from '@/stores/AuthStore';
import { basicPageProps, fetchTemplate, mobileProps } from '@/utils/isServer';
import { AutoComplete, Button, Form, Input, Modal, Select } from 'antd';
import dayjs from 'dayjs';
import Head from 'next/head';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MemberLicensePlates from '@/components/MemberLicensePlates';
import MemberCredits from '@/components/MemberCredits';
import { useRouter } from 'next/router';
import usePrefix from '@/hooks/usePrefix';
import ReCAPTCHA from "react-google-recaptcha";

const { Option } = Select;

export async function getServerSideProps(ctx) {
  const { isMobile } = mobileProps(ctx);
  const { configInfo, airports } = await basicPageProps(ctx);

  return {
    props: {
      airports,
      configInfo,
      deviceType: isMobile ? 'mobile' : 'desktop',
      recaptchaSiteKey: configInfo.recaptchaSiteKey,
    },
  };
}

function Account({ airports, configInfo, deviceType, recaptchaSiteKey }) {
  const { authStore, myReservationStore } = useMobxStores(),
    user: IAuthUser = authStore.user,
    [form] = Form.useForm(),
    autocompleteAirportOptions = airports?.result?.map((airport) => {
      return {
        value: `${airport.airport_initials} - ${airport.airport_name}`,
        id: airport.airport_id,
        airportinitials: airport.airport_initials,
      };
    });

  const captchaRef = useRef(null);
  const handleFilterAutocomplete = (inputValue, option) =>
    option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;

  const onFinish = (values: any) => {
    if (values.member_new_password) {
      Modal['confirm']({
        title: 'Password Change',
        content: 'These changes will also update your password',
        cancelText: 'CANCEL',
        okText: 'CONFIRM',

        onCancel: () => {
          return false;
        },
        onOk: () => {
          values.member_password = values.member_new_password;
          doUpdateAccount(values);
        },
      });
      return;
    }
    doUpdateAccount(values);
  };
  const doUpdateAccount = (values: any) => {
    values.member_login = myReservationStore.accountInfo?.member_login;
    values.member_email = myReservationStore.accountInfo?.member_email;
    fetch('/api/Member/UpdateAccount', {
      ...fetchTemplate,
      method: 'POST',
      body: JSON.stringify(values),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(() => {
        modalType({
          type: 'success',
          title: 'Profile updated!',
          content: <p>Your profile is updated now.</p>,
        });
      })
      .catch((error) => {
        modalType({
          type: 'error',
          title: 'Sorry!',
          content: (
            <p>
              An error occurred saving your profile.Please, try again later.
            </p>
          ),
        });
      });
  };

  const onFinishFailed = (errorInfo: any) => {
    ;//console.log('Failed:', errorInfo);
  };

  const loadInfo = useCallback(async () => {
    await myReservationStore.loadAccountInfo(user.member_id);
    form.setFieldsValue({
      member_first_name: myReservationStore.accountInfo?.member_first_name,
      member_last_name: myReservationStore.accountInfo?.member_last_name,
      member_phone: myReservationStore.accountInfo?.member_phone,
      member_birth_date: dayjs(
        myReservationStore.accountInfo?.member_birth_date
      ),
      member_gender: myReservationStore.accountInfo?.member_gender,
      member_country: myReservationStore.accountInfo?.member_country,
      member_city: myReservationStore.accountInfo?.member_city,
      home_airport: myReservationStore.accountInfo?.home_airport,
      member_zip: myReservationStore.accountInfo?.member_zip,
      member_address: myReservationStore.accountInfo?.member_address,
      disable_sms: myReservationStore.accountInfo?.disable_sms,
      member_login: myReservationStore.accountInfo?.member_login,
      member_password: myReservationStore.accountInfo?.member_password,
      member_id: myReservationStore.accountInfo?.member_id,
      member_email: myReservationStore.accountInfo?.member_email,
      member_valid_email: myReservationStore.accountInfo?.member_valid_email,
      member_picture: myReservationStore.accountInfo?.member_picture,
      member_type: myReservationStore.accountInfo?.member_type,
      member_active: myReservationStore.accountInfo?.member_active,
      member_premium: myReservationStore.accountInfo?.member_premium,
      member_dt_inc: myReservationStore.accountInfo?.member_dt_inc,
      member_key: myReservationStore.accountInfo?.member_key,
      member_dt_first_payment:
        myReservationStore.accountInfo?.member_dt_first_payment,
      member_dt_next_payment:
        myReservationStore.accountInfo?.member_dt_next_payment,
      member_fee: myReservationStore.accountInfo?.member_fee,
      yearly_fee_paid: myReservationStore.accountInfo?.yearly_fee_paid,
    });
  }, [form, myReservationStore, user]);

  const [autoCompleteResult, setAutoCompleteResult] = useState<string[]>([]);

  const handleAutoCompleteAirportChange = (value: string) => {
    if (!value) {
      setAutoCompleteResult([]);
    } else {
      setAutoCompleteResult(
        airports?.result?.map((airport) => {
          return {
            value: `${airport.airport_initials} - ${airport.airport_name}`,
            id: airport.airport_id,
            airportinitials: airport.airport_initials,
          };
        })
      );
    }
  };

  useEffect(() => {
    if (user) {
      loadInfo();
    }
  }, [loadInfo, user]);

  return (
    <>
      <Head>
        <title>My Account | On Air Parking</title>
        <meta name="description" content="Information about your account" />
      </Head>
      <ContentHeader btitle="My Account" />
      <div className="container mx-auto max-w-3xl mt-8">
        <div className="col-span-2 self-start flex flex-col w-full pb-6 px-4">
          <Form
            form={form}
            name="myaccount"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout={deviceType === 'mobile' ? 'horizontal' : 'vertical'}
            className="w-full"
            scrollToFirstError
          >
            <Form.Item name="member_password" hidden>
              <Input id="member_password" type="hidden" />
            </Form.Item>
            <Form.Item name="member_id" hidden>
              <Input id="member_id" type="hidden" />
            </Form.Item>
            <Form.Item name="member_email" hidden>
              <Input id="member_email" type="hidden" />
            </Form.Item>
            <Form.Item name="member_valid_email" hidden>
              <Input id="member_valid_email" type="hidden" />
            </Form.Item>
            <Form.Item name="member_picture" hidden>
              <Input id="member_picture" type="hidden" />
            </Form.Item>
            <Form.Item name="member_type" hidden>
              <Input id="member_type" type="hidden" />
            </Form.Item>
            <Form.Item name="member_active" hidden>
              <Input id="member_active" type="hidden" />
            </Form.Item>
            <Form.Item name="member_premium" hidden>
              <Input id="member_premium" type="hidden" />
            </Form.Item>
            <Form.Item name="member_dt_inc" hidden>
              <Input id="member_dt_inc" type="hidden" />
            </Form.Item>
            <Form.Item name="member_key" hidden>
              <Input id="member_key" type="hidden" />
            </Form.Item>
            <Form.Item name="member_dt_first_payment" hidden>
              <Input id="member_dt_first_payment" type="hidden" />
            </Form.Item>
            <Form.Item name="member_dt_next_payment" hidden>
              <Input id="member_dt_next_payment" type="hidden" />
            </Form.Item>
            <Form.Item name="member_fee" hidden>
              <Input id="member_fee" type="hidden" />
            </Form.Item>
            <Form.Item name="yearly_fee_paid" hidden>
              <Input id="yearly_fee_paid" type="hidden" />
            </Form.Item>
            <Form.Item name="sks" hidden>
              <Input id="sks" type="hidden" />
            </Form.Item>

            {/** This is for stripe? */}
            {/* <input type="hidden" name="sks" id="sks" value="pk_test_cdeatOe23EUppfwNzWMcK3TT"></input> */}

            <div className="border border-primary rounded-md overflow-hidden">
              <div className="text-sm text-primary bg-orange-50 border-b border-primary  flex flex-row items-center w-full relative p-3">
                <span className="font-medium rounded-full h-6 w-6 bg-white flex flex-row justify-center items-center">
                  1
                </span>
                <span className="ml-2">&nbsp;Basic Information</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 px-3">
                <div className="flex flex-col">
                  <Form.Item
                    name="member_first_name"
                    label="First Name"
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message: 'Please input your First Name!',
                      },
                    ]}
                    htmlFor="member_first_name"
                  >
                    <Input
                      id="member_first_name"
                      className="border border-gray-300 w-full"
                    />
                  </Form.Item>
                </div>
                <div className="flex flex-col">
                  <Form.Item
                    name="member_last_name"
                    label="Last Name"
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message: 'Please input your Last Name!',
                      },
                    ]}
                    htmlFor="member_last_name"
                  >
                    <Input
                      id="member_last_name"
                      className="border border-gray-300 w-full"
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 px-3">
                <div className="flex flex-col">
                  <Form.Item
                    name="member_birth_date"
                    label="Birth Date"
                    rules={[
                      {
                        type: 'date',
                        required: true,
                        message: 'Please input your Birth Date!',
                      },
                    ]}
                    htmlFor="member_birth_date"
                  >
                    <DatePicker
                      inputReadOnly
                      // onChange={onChange}
                      // onOk={onOk}
                      use12Hours={true}
                      id="member_birth_date"
                      name="member_birth_date"
                      className="w-full"
                    />
                  </Form.Item>
                </div>
                {/* <div className="flex flex-col">
                  <Form.Item
                    name="member_gender"
                    label="Gender"
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message: 'Please select your Gender!',
                      },
                    ]}
                    htmlFor="member_gender"
                  >
                    <Select
                      id="member_gender"
                      value="M"
                      bordered={false}
                      placeholder="Select one"
                      className="border rounded-md"
                    >
                      <Option value="M">Male</Option>
                      <Option value="F">Female</Option>
                      <Option value="O">Other</Option>
                    </Select>
                  </Form.Item>
                </div> */}
                <div className="flex flex-col">
                  <Form.Item
                    name="member_phone"
                    label="Phone"
                    htmlFor="member_phone"
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message: 'Please input your Phone!',
                      },
                    ]}
                  >
                    <Input
                      id="member_phone"
                      className="border border-gray-300 w-full"
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 px-3">
                <div className="flex flex-col">
                  <Form.Item
                    name="member_country"
                    label="Country"
                    htmlFor="member_country"
                  >
                    <Input
                      id="member_country"
                      className="border border-gray-300 w-full"
                    />
                  </Form.Item>
                </div>
                <div className="flex flex-col">
                  <Form.Item
                    name="member_city"
                    label="City"
                    htmlFor="member_city"
                  >
                    <Input
                      id="member_city"
                      className="border border-gray-300 w-full"
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 px-3">
                <div className="flex flex-col">
                  <Form.Item
                    name="member_address"
                    label="Address"
                    htmlFor="member_address"
                  >
                    <Input
                      id="member_address"
                      className="border border-gray-300 w-full"
                    />
                  </Form.Item>
                </div>
                <div className="flex flex-col">
                  <Form.Item
                    name="member_zip"
                    label="ZipCode"
                    htmlFor="member_zip"
                  >
                    <Input
                      id="member_zip"
                      className="border border-gray-300 w-full"
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 px-3">
                <div className="flex flex-col">
                  <Form.Item
                    name="disable_sms"
                    label="SMS Status"
                    htmlFor="disable_sms"
                  >
                    <Select
                      placeholder="Select one"
                      className="border rounded-md"
                      aria-label="Type of Business"
                    >
                      <Select.Option value=""> </Select.Option>
                      <Select.Option value={0}>Enabled</Select.Option>
                      <Select.Option value={1}>Disabled</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-8 px-3">
                <div className="flex flex-col">
                  <Form.Item
                    name="home_airport"
                    label="Home Airport"
                    htmlFor="home_airport"
                  >
                    <AutoComplete
                      className="border rounded-md border-gray-300 w-full"
                      options={autocompleteAirportOptions}
                      filterOption={(inputValue, option) =>
                        handleFilterAutocomplete(inputValue, option)
                      }
                      onChange={handleAutoCompleteAirportChange}
                    >
                      <Input
                        bordered={false}
                        className="border-none hover:border-white px-3"
                        placeholder="Where do you want to park?"
                      />
                    </AutoComplete>
                  </Form.Item>
                </div>
              </div>

              <div className="text-sm text-primary flex flex-row bg-orange-50 border-b border-t border-primary p-3 items-center w-full relative">
                <span className="font-medium rounded-full h-6 w-6 bg-white flex flex-row justify-center items-center">
                  2
                </span>
                <span className="ml-2">&nbsp;Login and Password</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 p-3">
                <div className="flex flex-col md:col-span-2">
                  <Form.Item name="member_login" label="Login" hasFeedback>
                    <Input
                      id="member_login"
                      type="text"
                      readOnly={true}
                      disabled={true}
                    />
                  </Form.Item>
                </div>
                <div className="flex flex-col">
                  <Form.Item
                    name="member_new_password"
                    label="New Password"
                    hasFeedback
                  >
                    <Input.Password
                      id="member_new_password"
                      autoComplete="new-password"
                    />
                  </Form.Item>
                </div>
                <div className="flex flex-col">
                  <Form.Item
                    name="member_confirm_password"
                    label="Confirm Password"
                    dependencies={['member_new_password']}
                    hasFeedback
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            !value ||
                            getFieldValue('member_new_password') === value
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(
                              'The two passwords that you entered do not match!'
                            )
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      id="member_confirm_password"
                      autoComplete="new-password"
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
            <Button
              type="primary"
              className="mt-4 mb-7 uppercase"
              htmlType="submit"
            >
              Save Account
            </Button>
          </Form>

          <MemberCredits credits={myReservationStore?.rebookReservations} />

          <div className="flex flex-col py-4">
            {!!recaptchaSiteKey && (
              <ReCAPTCHA
                sitekey={recaptchaSiteKey}
                ref={captchaRef}
                size="invisible"
              />
            )}
          </div>
          <MemberCardKeys
            captchaRef={captchaRef}
            user={myReservationStore.rebookReservations}
            configInfo={configInfo}
          />
          <MemberLicensePlates user={user} configInfo={configInfo} />

          {/* <MembershipCard
            user={user}
            hasMemberTokenSubscription={
              myReservationStore.hasMemberTokenSubscription
            }
            loadInfo={loadInfo}
            configInfo={configInfo}
          /> */}
        </div>
      </div>
    </>
  );
}

Account.layout = 'PrivateLayout';

export default observer(Account);
